import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, Save, XCircle, AlertCircle, CheckCircle, Upload, Image as ImageIcon, DollarSign } from 'lucide-react';
import { supabase } from '../../utils/supabase';

const OffersManagement = () => {
  const DEFAULT_OFFER_TYPE = 'discount';
  const DEFAULT_OFFER_CATEGORY = 'food';

  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    original_price: '',
    image_url: '',
    start_date: '',
    end_date: '',
    type: DEFAULT_OFFER_TYPE,
    category_id: null,
    is_active: true
  });
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const RECOMMENDED_DIMENSIONS = "800x600px (relación de aspecto 4:3)";

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      price: '',
      original_price: '',
      image_url: '',
      start_date: '',
      end_date: '',
      type: DEFAULT_OFFER_TYPE,
      category_id: null,
      is_active: true
    });
  };

  const getCategoryName = (offer) => {
    if (!offer.category_id) return 'Sin categoría';

    const category = categories.find(cat => cat.id === offer.category_id);
    return category ? category.category_name : 'Sin categoría';
  };

  useEffect(() => {
    const lastFetch = localStorage.getItem('offers_last_fetch');
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    if (!lastFetch || (now - parseInt(lastFetch)) > fiveMinutes) {
      fetchOffers();
    } else {
      const cachedOffers = localStorage.getItem('offers_cache');
      if (cachedOffers) {
        setOffers(JSON.parse(cachedOffers));
        setLoading(false);
      } else {
        fetchOffers();
      }
    }

    // Cargar categorías
    fetchCategories();
  }, []);

  const fetchOffers = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    console.log('Fetching offers from Supabase...');

    try {
      // Agregar timeout para evitar que se quede cargando indefinidamente
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout: La consulta tardó demasiado')), 15000)
      );

      const queryPromise = supabase
        .from('offers')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

      if (error) {
        console.error('Error fetching offers:', error);
        setError(`Error al cargar las ofertas: ${error.message}`);
        setOffers([]);
      } else {
        console.log('Offers fetched successfully:', data?.length || 0, 'ofertas');
        const offersData = data || [];
        setOffers(offersData);

        localStorage.setItem('offers_cache', JSON.stringify(offersData));
        localStorage.setItem('offers_last_fetch', Date.now().toString());
      }
    } catch (error) {
      console.error('Network error fetching offers:', error);
      setError(`Error de conexión: ${error.message}`);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      console.log('Cargando categorías...');

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout al cargar categorías')), 10000)
      );

      const queryPromise = supabase
        .from('offers_categories')
        .select('*')
        .order('category_name', { ascending: true });

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

      if (error) {
        console.error('Error fetching categories:', error);
        // Datos de ejemplo si hay error
        setCategories([
          { id: 1, category_name: 'Restaurantes', description: 'Ofertas de restaurantes y comida' },
          { id: 2, category_name: 'Desayunos', description: 'Ofertas de desayunos y panadería' },
          { id: 3, category_name: 'Recomendación', description: 'Ofertas recomendadas especiales' }
        ]);
      } else {
        console.log('Categorías cargadas:', data?.length || 0);
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Datos de ejemplo si hay error
      setCategories([
        { id: 1, category_name: 'Restaurantes', description: 'Ofertas de restaurantes y comida' },
        { id: 2, category_name: 'Desayunos', description: 'Ofertas de desayunos y panadería' },
        { id: 3, category_name: 'Recomendación', description: 'Ofertas recomendadas especiales' }
      ]);
    }
  };

  const testSupabaseConnection = async () => {
    console.log('Testing Supabase connection...');
    setMessage({ type: 'info', text: 'Probando conexión...' });

    try {
      const { data: readData, error: readError } = await supabase
        .from('offers')
        .select('*')
        .limit(1);

      if (readError) {
        console.error('Read test failed:', readError);
        setMessage({ type: 'error', text: `Error de lectura: ${readError.message}` });
        return;
      }

      const testOffer = {
        title: 'Test Offer',
        description: 'Test Description',
        discount: 'Test Discount',
        price: 10.00,
        original_price: 15.00,
        start_date: '2025-01-01',
        end_date: '2025-01-31',
        is_active: false
      };

      const { data: insertData, error: insertError } = await supabase
        .from('offers')
        .insert([testOffer])
        .select();

      if (insertError) {
        console.error('Insert test failed:', insertError);
        setMessage({ type: 'error', text: `Error de inserción: ${insertError.message}` });
        return;
      }

      if (insertData && insertData[0]) {
        await supabase
          .from('offers')
          .delete()
          .eq('id', insertData[0].id);
      }

      console.log('All tests passed successfully');
      setMessage({ type: 'success', text: 'Todas las pruebas de conexión exitosas.' });

    } catch (error) {
      console.error('Unexpected error testing connection:', error);
      setMessage({ type: 'error', text: `Error inesperado: ${error.message}` });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('🖼️ Iniciando subida de imagen para oferta:', file.name);
    setUploading(true);
    setMessage(null);

    try {
      const filePath = `public/offers/${Date.now()}_${file.name}`;
      const bucketName = 'images-club-mia';

      console.log('📁 Subiendo a:', bucketName, filePath);

      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('❌ Error uploading image:', error);
        setMessage({ type: 'error', text: 'Error al subir la imagen: ' + error.message });
        return;
      }

      console.log('✅ Imagen subida exitosamente:', data);

      const publicURL = supabase.storage.from(bucketName).getPublicUrl(filePath).data.publicUrl;
      console.log('🔗 URL pública generada:', publicURL);

      // Actualizar el formulario con la nueva URL
      setForm((prevForm) => {
        const newForm = { ...prevForm, image_url: publicURL };
        console.log('📝 Formulario de oferta actualizado:', newForm);
        return newForm;
      });

      // Si estamos editando una oferta existente, actualizar la DB inmediatamente
      if (editingOffer) {
        console.log('🔄 Actualizando URL de imagen en la DB para oferta existente...');
        try {
          const { data, error } = await supabase
            .from('offers')
            .update({ image_url: publicURL })
            .eq('id', editingOffer.id)
            .select();

          if (error) {
            console.error('❌ Error actualizando imagen en DB:', error);
            setMessage({ type: 'warning', text: 'Imagen subida, pero error al actualizar en la base de datos: ' + error.message });
          } else {
            console.log('✅ URL de imagen actualizada en DB:', data[0]);
            // Actualizar el item en la lista
            setOffers(offers.map((offer) => (offer.id === editingOffer.id ? data[0] : offer)));
            setMessage({ type: 'success', text: 'Imagen subida y actualizada en la base de datos automáticamente.' });
          }
        } catch (dbError) {
          console.error('💥 Error inesperado actualizando DB:', dbError);
          setMessage({ type: 'warning', text: 'Imagen subida, pero error inesperado al actualizar la base de datos.' });
        }
      } else {
        setMessage({ type: 'success', text: 'Imagen subida con éxito. Recuerda guardar la oferta para completar la creación.' });
      }

    } catch (error) {
      console.error('💥 Error inesperado:', error);
      setMessage({ type: 'error', text: 'Error inesperado al subir la imagen: ' + error.message });
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setForm({
      title: offer.title,
      description: offer.description || '',
      price: offer.price || '',
      original_price: offer.original_price || '',
      image_url: offer.image_url || '',
      start_date: offer.start_date || '',
      end_date: offer.end_date || '',
      type: offer.type || DEFAULT_OFFER_TYPE,
      category_id: offer.category_id || null,
      is_active: offer.is_active
    });
    setMessage(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta oferta?')) return;

    const { error } = await supabase
      .from('offers')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting offer:', error);
      setMessage({ type: 'error', text: 'Error al eliminar la oferta.' });
    } else {
      setOffers(offers.filter((offer) => offer.id !== id));
      setMessage({ type: 'success', text: 'Oferta eliminada con éxito.' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!form.title || !form.description) {
      setMessage({ type: 'error', text: 'Por favor, completa todos los campos requeridos (título y descripción).' });
      return;
    }

    const payload = {
      ...form,
      // Asegurar que los campos numéricos sean números o null
      price: form.price ? parseFloat(form.price) : null,
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      // Convertir fechas vacías a null para evitar errores de PostgreSQL
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      // Convertir campos de texto vacíos a null si es necesario
      image_url: form.image_url || null,
      // Asegurar que siempre se incluyan los valores por defecto
      type: DEFAULT_OFFER_TYPE,
      category_id: form.category_id,
    };

    console.log('🚀 Payload de oferta to send:', payload);
    console.log('📋 Current form state:', form);
    console.log('🖼️ Image URL in payload:', payload.image_url);

    try {
      if (editingOffer) {
        const { data, error } = await supabase
          .from('offers')
          .update(payload)
          .eq('id', editingOffer.id)
          .select();

        if (error) {
          console.error('❌ Error updating offer:', error);
          setMessage({ type: 'error', text: `Error al actualizar la oferta: ${error.message}` });
        } else {
          console.log('✅ Oferta actualizada exitosamente:', data[0]);
          console.log('🖼️ URL de imagen guardada en oferta:', data[0].image_url);
          setOffers(offers.map((offer) => (offer.id === editingOffer.id ? data[0] : offer)));
          setMessage({ type: 'success', text: 'Oferta actualizada con éxito.' });
          setEditingOffer(null);
          resetForm();
        }
      } else {
        const { data, error } = await supabase
          .from('offers')
          .insert([payload])
          .select();

        if (error) {
          console.error('Error adding offer:', error);
          setMessage({ type: 'error', text: `Error al añadir la oferta: ${error.message}` });
        } else {
          console.log('✅ Oferta añadida exitosamente:', data[0]);
          console.log('🖼️ URL de imagen guardada en nueva oferta:', data[0].image_url);
          setOffers([data[0], ...offers]);
          setMessage({ type: 'success', text: 'Oferta añadida con éxito.' });
          resetForm();
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setMessage({ type: 'error', text: `Error inesperado: ${error.message}` });
    }
  };

  const handleCancelEdit = () => {
    setEditingOffer(null);
    resetForm();
    setMessage(null);
  };

  if (loading) {
    return (
      <div className="p-8 flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        <p className="text-gray-600">Cargando ofertas...</p>
        <button
          onClick={() => {
            setLoading(false);
            fetchOffers();
          }}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancelar y reintentar
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-red-600 text-center">
          <h3 className="text-lg font-semibold mb-2">Error al cargar ofertas</h3>
          <p className="text-sm">{error}</p>
        </div>
        <button
          onClick={fetchOffers}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <motion.div
      className="p-8 bg-gray-50 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-4xl font-bold text-gray-800">Gestión de Ofertas</h2>
        <div className="flex gap-2">
          <button
            onClick={() => fetchOffers(true)}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Actualizar
          </button>
        </div>
      </div>

      {/* Formulario de Añadir/Editar Oferta */}
      <motion.div
        className="bg-white rounded-xl shadow-md p-6 mb-8"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-gray-800">
            {editingOffer ? 'Editar Oferta' : 'Añadir Nueva Oferta'}
          </h3>
          <button
            type="button"
            onClick={testSupabaseConnection}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
          >
            Probar Conexión
          </button>
        </div>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            name="title"
            placeholder="Título de la oferta"
            value={form.title}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          />

          <input
            type="number"
            name="price"
            placeholder="Precio actual (S/.)"
            value={form.price}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            step="0.01"
            min="0"
          />
          <input
            type="number"
            name="original_price"
            placeholder="Precio original (S/.)"
            value={form.original_price}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            step="0.01"
            min="0"
          />
          <textarea
            name="description"
            placeholder="Descripción de la oferta"
            value={form.description}
            onChange={handleInputChange}
            rows="3"
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 md:col-span-2"
            required
          ></textarea>

          {/* Select de Categoría */}
          <select
            name="category_id"
            value={form.category_id || ''}
            onChange={handleInputChange}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
            required
          >
            <option value="">Seleccionar categoría</option>
            {categories.filter(category => category.is_active).map(category => (
              <option key={category.id} value={category.id}>
                {category.category_name}
              </option>
            ))}
          </select>

          <input
            type="date"
            name="start_date"
            value={form.start_date}
            onChange={handleInputChange}
            placeholder="Fecha de inicio (opcional)"
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
          />
          <input
            type="date"
            name="end_date"
            value={form.end_date}
            onChange={handleInputChange}
            placeholder="Fecha de fin (opcional)"
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
          />

          {/* Campo de subida de imagen */}
          <div className="md:col-span-2">
            <label htmlFor="offer-image-upload" className="block text-sm font-medium text-gray-700 mb-2">
              Imagen de la Oferta ({RECOMMENDED_DIMENSIONS})
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="file"
                id="offer-image-upload"
                accept="image/*"
                onChange={handleImageUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-orange-50 file:text-orange-700
                  hover:file:bg-orange-100"
                disabled={uploading}
              />
              {uploading && (
                <svg className="animate-spin h-5 w-5 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
            {form.image_url && (
              <div className="mt-4 flex items-center space-x-2">
                <ImageIcon className="w-5 h-5 text-gray-500" />
                <a href={form.image_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate">
                  {form.image_url.split('/').pop()}
                </a>
              </div>
            )}
          </div>

          {/* Checkbox de Activo */}
          <div className="md:col-span-2 flex items-center">
            <input
              type="checkbox"
              name="is_active"
              id="is_active_offer"
              checked={form.is_active}
              onChange={handleInputChange}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active_offer" className="ml-2 block text-sm text-gray-900">
              Activo
            </label>
          </div>

          <div className="md:col-span-2 flex justify-end space-x-4 mt-4">
            {editingOffer && (
              <motion.button
                type="button"
                onClick={handleCancelEdit}
                className="flex items-center px-6 py-3 bg-gray-300 text-gray-800 rounded-xl font-semibold hover:bg-gray-400 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <XCircle className="w-5 h-5 mr-2" /> Cancelar
              </motion.button>
            )}
            <motion.button
              type="submit"
              className="flex items-center px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Save className="w-5 h-5 mr-2" /> {editingOffer ? 'Actualizar Oferta' : 'Añadir Oferta'}
            </motion.button>
          </div>
        </form>
        <AnimatePresence>
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mt-4 p-3 rounded-xl flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-green-100 text-green-700'
                  : message.type === 'info'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              {message.text}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Lista de Ofertas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {Array.isArray(offers) && offers.map((offer) => (
            <motion.div
              key={offer.id}
              layout
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
              transition={{ type: "spring", stiffness: 100, damping: 10, duration: 0.5 }}
              className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
            >
              <img src={offer.image_url} alt={offer.title} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{offer.title}</h3>
                <p className="text-gray-600 text-sm mb-2 line-clamp-2">{offer.description}</p>
                <div className="flex items-center text-gray-500 text-sm mb-2">
                  <span>{offer.discount}</span>
                </div>

                {/* Precios */}
                {(offer.price || offer.original_price) && (
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <div className="flex items-center gap-2">
                      {offer.price && (
                        <span className="text-lg font-bold text-green-600">S/. {offer.price}</span>
                      )}
                      {offer.original_price && (
                        <span className="text-sm text-gray-500 line-through">S/. {offer.original_price}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center text-gray-500 text-sm mb-2">
                  <span>{offer.start_date} - {offer.end_date}</span>
                </div>

                {/* Categoría */}
                <div className="flex items-center text-gray-500 text-sm mb-2">
                  <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                    {getCategoryName(offer)}
                  </span>
                </div>

                <div className="flex items-center text-gray-500 text-sm mb-2">
                  {offer.is_active ? (
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 mr-2" />
                  )}
                  <span>{offer.is_active ? 'Activo' : 'Inactivo'}</span>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <motion.button
                    onClick={() => handleEdit(offer)}
                    className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Edit className="w-5 h-5" />
                  </motion.button>
                  <motion.button
                    onClick={() => handleDelete(offer.id)}
                    className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Trash2 className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default OffersManagement;